import axios from 'axios';

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_URL, // <-- use VITE_API_URL (not VITE_url)
  withCredentials: true,
  withXSRFToken: true,            // send/receive cookies for Sanctum
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  }
});

// Response interceptor — handles global error cases (401 / 403 / 5xx) so
// every caller doesn't have to. 4xx (other than 401/403) is left for
// callers to handle with context-aware messages. See
// docs/infrastructure/error-handling.md.
//
// Opt-out per call with `meta: { silent: true }` — the only thing left to
// suppress is the 401 → /login redirect. Use for passive background calls
// (session status checks, prefetches, polling) and for crash reporting,
// where a redirect would replace the screen the user is reading.
//
// Loading state is intentionally NOT tracked here. The previous
// global loading bar was driven by counting in-flight requests; it
// fired for every passive plumbing call (session check, prefetch,
// etc.) and made unrelated pages feel "busy." Loading state is now
// component-local: each submit handler owns its own `submitting`
// flag and renders a button spinner / disabled state inline. The
// route-level Suspense fallback covers page transitions. See
// `pages/users/ProfilePage.jsx` for the canonical inline pattern.
http.interceptors.response.use(
  (response) => response,
  (error) => {
    const status  = error.response?.status;
    const data    = error.response?.data ?? {};
    const silent  = error.config?.meta?.silent;

    // Always log the failure — helps debugging without changing UX
     
    console.error('[http]', error.config?.method?.toUpperCase(), error.config?.url, status, data);

    if (!silent) {
      if (status === 401) {
        // Session expired mid-flow — kick to login. The session-status
        // check (/api/auth/status) is intentionally PUBLIC and returns
        // 200 with { authenticated: false } for anonymous visitors, so
        // a 401 reaching this branch can only mean "you were
        // authenticated but your session just expired." That's the
        // single case where the hard redirect is correct.
        //
        // Skip on /login itself so failed-login attempts (wrong
        // password → 401) keep their error visible instead of bouncing.
        const onLogin = window.location.pathname.startsWith('/login');
        if (!onLogin) {
          window.location.href = '/login';
        }
      }
      // NOTHING ELSE IS DISPLAYED HERE — deliberately.
      //
      // This interceptor used to toast 403 and every 5xx. At this layer a
      // background list refresh and a payment submit look identical, so it
      // announced both the same way; pages that also rendered the error inline
      // then showed one failure twice — observed as three messages for one 500
      // on the payment page.
      //
      // Display now belongs to the layer that can tell those apart:
      //   queryCache.onError    — background loads, nothing on screen → toast
      //   mutationCache.onError — user actions, unless the caller claimed it
      //   notifyApiError        — raw try/catch calls not yet on React Query
      // See src/app/queryClient.js.
      //
      // The 401 redirect stays because only this layer can do it: it is a
      // navigation rather than a message, and it must fire for every request
      // regardless of which layer above made it.
    }

    return Promise.reject(error);
  }
);

axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;
http.defaults.xsrfCookieName = 'XSRF-TOKEN';
http.defaults.xsrfHeaderName  = 'X-XSRF-TOKEN';
